import React, { useState, useEffect } from 'react';
import axiosInstance from '../config/axios';
import { Download, Trash2, ChevronDown, Pencil } from 'lucide-react';
import './ArchivedCounts.css';

const ArchivedCounts = () => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingPeriodId, setEditingPeriodId] = useState(null);
  const [editNameInput, setEditNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/api/tenant/counts/archived-periods');
      setPeriods(response.data.periods || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load archived periods');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (periodId) => {
    setExporting(periodId);
    setError('');
    try {
      const response = await axiosInstance.get(`/api/tenant/counts/export-archived/${periodId}`, {
        responseType: 'blob'
      });
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/zip' });
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Archived_Counts_${new Date().toISOString().split('T')[0]}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/i);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export archived counts');
    } finally {
      setExporting(null);
    }
  };

  const handleDelete = async (periodId) => {
    const period = periods.find(p => p.id === periodId);
    const dateStr = period?.archivedAt ? new Date(period.archivedAt).toLocaleString() : 'this period';
    if (!window.confirm(`Are you sure you want to permanently delete the archived period from ${dateStr}? This cannot be undone.`)) {
      return;
    }
    setDeleting(periodId);
    setError('');
    try {
      await axiosInstance.delete(`/api/tenant/counts/archived-periods/${periodId}`);
      await fetchPeriods();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete archived period');
    } finally {
      setDeleting(null);
    }
  };

  const openEditModal = (period) => {
    setEditingPeriodId(period.id);
    setEditNameInput(period.periodName != null && String(period.periodName).trim() ? period.periodName.trim() : '');
    setError('');
  };

  const closeEditModal = () => {
    setEditingPeriodId(null);
    setEditNameInput('');
  };

  const handleEditSave = async () => {
    if (editingPeriodId == null) return;
    setSavingName(true);
    setError('');
    try {
      const name = typeof editNameInput === 'string' ? editNameInput.trim() : '';
      await axiosInstance.patch(`/api/tenant/counts/archived-periods/${editingPeriodId}`, {
        periodName: name === '' ? null : name
      });
      await fetchPeriods();
      closeEditModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update archive name');
    } finally {
      setSavingName(false);
    }
  };

  const getPeriodDisplayName = (period) => {
    if (period.periodName && String(period.periodName).trim()) return period.periodName.trim();
    if (!period.archivedAt) return 'Archive';
    return formatDate(period.archivedAt);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedPeriods = () => {
    if (!sortColumn) return periods;
    return [...periods].sort((a, b) => {
      let aValue, bValue;
      switch (sortColumn) {
        case 'date':
          aValue = new Date(a.archivedAt).getTime();
          bValue = new Date(b.archivedAt).getTime();
          break;
        case 'totalCount':
          aValue = a.totalCount ?? a.count ?? 0;
          bValue = b.totalCount ?? b.count ?? 0;
          break;
        case 'itemCount':
          aValue = a.count ?? 0;
          bValue = b.count ?? 0;
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader = ({ column, children }) => (
    <th className="ac-sortable-th" onClick={() => handleSort(column)} title={'Sort by ' + children}>
      <button type="button" className="ac-sort-btn">
        <span>{children}</span>
        <ChevronDown size={10} className={'ac-sort-icon' + (sortColumn === column ? ' active' : '') + (sortColumn === column && sortDirection === 'desc' ? ' desc' : '')} />
      </button>
    </th>
  );

  if (loading) {
    return <div className="ac-loading">Loading archived periods...</div>;
  }

  return (
    <div>
      <div className="ac-section-header">
        <div>
          <h2 className="ac-section-title">Archived Counting Periods</h2>
          <p className="ac-section-subtitle">View and export past counting periods</p>
        </div>
        <button className="btn btn-secondary ac-refresh-btn" onClick={fetchPeriods} disabled={loading}>
          Refresh
        </button>
      </div>

      <div className="ac-stats-row">
        <div className="ac-stat-card">
          <span className="ac-stat-label">TOTAL PERIODS</span>
          <span className="ac-stat-value">{periods.length}</span>
        </div>
      </div>

      {error && <div className="ac-error">{error}</div>}

      <div className="card ac-table-card">
        <div className="ac-table-scroll">
          <table className="table ac-table">
            <thead>
              <tr>
                <SortableHeader column="date">Period</SortableHeader>
                <SortableHeader column="totalCount">Total Count</SortableHeader>
                <SortableHeader column="itemCount">Item Count</SortableHeader>
                <th className="ac-actions-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {periods.length === 0 ? (
                <tr>
                  <td colSpan="4" className="ac-empty-state">
                    No archived counting periods yet. When you start a new counting period, completed counts are archived here.
                  </td>
                </tr>
              ) : (
                getSortedPeriods().map((period) => (
                  <tr key={period.id} className="ac-row">
                    <td className="ac-meta-cell" title={formatDate(period.archivedAt)}>{getPeriodDisplayName(period)}</td>
                    <td className="ac-meta-cell">{period.totalCount ?? period.count ?? (period.counts?.length ?? 0)}</td>
                    <td className="ac-meta-cell">{period.count}</td>
                    <td>
                      <div className="ac-actions">
                        <button
                          type="button"
                          className="ac-action-btn ac-action-edit"
                          onClick={() => openEditModal(period)}
                          title="Edit archive name"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          className="ac-action-btn ac-action-export"
                          onClick={() => handleExport(period.id)}
                          disabled={exporting === period.id}
                          title={exporting === period.id ? 'Exporting...' : 'Export to ZIP'}
                        >
                          <Download size={14} />
                        </button>
                        <button
                          type="button"
                          className="ac-action-btn ac-action-delete"
                          onClick={() => handleDelete(period.id)}
                          disabled={deleting === period.id}
                          title={deleting === period.id ? 'Deleting...' : 'Delete'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingPeriodId && (
        <div className="ac-modal-backdrop" onClick={closeEditModal}>
          <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ac-modal-title">Edit archive name</h3>
            <input
              type="text"
              className="ac-modal-input"
              value={editNameInput}
              onChange={(e) => setEditNameInput(e.target.value)}
              maxLength={25}
              placeholder="Leave blank for date-based label"
              autoFocus
            />
            <div className="ac-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeEditModal} disabled={savingName}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleEditSave} disabled={savingName}>
                {savingName ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchivedCounts;